import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'next-i18next';
import { IUserLocation, IPointOfPresence, IGoogleMapLocation } from '@features/checkout/typescript';
import styles from '@features/showrooms/sections/ShowroomsSection/Components/GoogleMap/GoogleMap.styles';
import { BASE_PATH, SHOWROOMS_MAP_DEFAULT_CONFIG } from '@config/index';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
let markerClusterer: MarkerClusterer;
type PropsType = {
  map: google.maps.Map | null;
  setMap: Dispatch<SetStateAction<google.maps.Map | null>>;
  loader: Loader;
  active: boolean;
  size: 'small' | 'medium' | 'large';
  points: Array<IPointOfPresence>;
  point: IPointOfPresence | null;
  disabledPoints: Array<IPointOfPresence>;
  userLocation: IUserLocation;
  searchLocation: IGoogleMapLocation;
  setPointUuid: (newShowroomUuid: string) => void;
};
const GoogleMap = ({
  map,
  setMap,
  loader,
  active,
  size,
  points,
  point,
  disabledPoints,
  userLocation,
  searchLocation,
  setPointUuid
}: PropsType) => {
  const [markers, setMarkers] = useState<Record<string, google.maps.Marker>>({});
  const [oldPoint, setOldPoint] = useState<IPointOfPresence | null>(null);
  const {
    t
  } = useTranslation('common');
  const defaultZoom = SHOWROOMS_MAP_DEFAULT_CONFIG.zoom;
  const defaultCenterLocation: google.maps.LatLngLiteral = {
    lat: SHOWROOMS_MAP_DEFAULT_CONFIG.lat,
    lng: SHOWROOMS_MAP_DEFAULT_CONFIG.lng
  };
  const {
    getLayoutBarsTotalHeight
  } = layoutUiState;
  const googlemapRef = useRef<HTMLDivElement | null>(null);
  const getMarkerIcon = (p: IPointOfPresence, _pointUuid: string | null): string => {
    const sufix: string = _pointUuid && p.uuid === _pointUuid ? '_on' : '';
    if (p?.group?.code) {
      if (p?.group?.code === 'VOX_INTERIORS') {
        return `${BASE_PATH}/img/markers/marker-interiors${sufix}.svg`;
      }
      if (p?.group?.code === 'VOX_FURNITURES') {
        return `${BASE_PATH}/img/markers/marker-furniture${sufix}.svg`;
      }
      if (p?.group?.code === 'VOX_DIP') {
        return `${BASE_PATH}/img/markers/marker-dip${sufix}.svg`;
      }
      if (p?.group?.code === 'VOXBOX_STUDIO') {
        return `${BASE_PATH}/img/markers/marker-voxbox${sufix}.svg`;
      }
    }
    return `${BASE_PATH}/img/markers/marker-interiors${sufix}.svg`;
  };
  const getRenderMarkerClusterer = (disabled: boolean) => {
    if (disabled) {
      return {
        render: ({
          position
        }) => new google.maps.Marker({
          label: null,
          icon: `${BASE_PATH}/img/markers/marker-empty.svg`,
          position,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX),
          clickable: true,
          optimized: true
        })
      };
    }
    return {
      render: ({
        count,
        position
      }) => new google.maps.Marker({
        label: {
          text: String(count),
          color: 'white',
          fontSize: '18px'
        },
        icon: `${BASE_PATH}/img/markers/marker-group.png`,
        position,
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count + 1,
        anchorPoint: new google.maps.Point(22, 64),
        clickable: true,
        optimized: true
      })
    };
  };
  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };
  const onClickMarker = (uuid: string | undefined) => {
    if (uuid) {
      exitFullscreen();
      setPointUuid(uuid);
    }
  };
  const centerMapOnUserCountry = (newMap: google.maps.Map) => {
    // Geocoder componentRestrictions.country expects ISO 3166-1 alpha-2 (e.g. 'PL'), not a country name
    const countryRestriction = userLocation?.countryCode || userLocation?.country || 'PL';
    if (newMap && countryRestriction) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({
        componentRestrictions: {
          country: countryRestriction
        }
      }, (results, status) => {
        if (status === 'OK' && results?.[0]?.geometry?.bounds) {
          newMap.fitBounds(results[0].geometry.bounds, 10);
        } else {
          newMap.setCenter(defaultCenterLocation);
          newMap.setZoom(defaultZoom);
        }
      });
    } else if (newMap) {
      newMap.setCenter(defaultCenterLocation);
      newMap.setZoom(defaultZoom);
    }
  };
  const getPointsLatLng = (country: string | null, countryCode: string | null | undefined) => {
    const newPointsLatLng: google.maps.LatLng[] = [];
    points.forEach(p => {
      if (p?.location?.latitude && p?.location?.longitude) {
        if (!country && !countryCode || country && p?.country?.name && country.toUpperCase() === p.country.name.toUpperCase() || countryCode && p?.country?.code && countryCode.toUpperCase() === p.country.code.toUpperCase()) {
          newPointsLatLng.push(new google.maps.LatLng(p.location.latitude, p.location.longitude));
        }
      }
    });
    return newPointsLatLng;
  };
  const getPointsLatLngForBounds = () => {
    let pointsLatLng = getPointsLatLng(userLocation.country, userLocation.countryCode);
    // If geoIP country has no matching points (e.g. user in US, showrooms only in PL), use all points
    if (pointsLatLng.length === 0) {
      pointsLatLng = getPointsLatLng(null, null);
    }
    return pointsLatLng;
  };
  const fitMapToPoints = (targetMap: google.maps.Map, pointsLatLng: google.maps.LatLng[]) => {
    const bounds = new google.maps.LatLngBounds();
    pointsLatLng.forEach(latLng => bounds.extend(latLng));
    targetMap.fitBounds(bounds, 80);
    targetMap.panTo(bounds.getCenter());
    const zoom = targetMap.getZoom();
    if (zoom && zoom > 12) {
      targetMap.setZoom(12);
    }
  };
  useEffect(() => {
    if (map && point?.uuid && markers[point.uuid]) {
      points.forEach(p => {
        if (p?.uuid && point?.uuid && p?.location?.latitude && p?.location?.longitude) {
          markers[p.uuid].setIcon(getMarkerIcon(p, point.uuid));
        }
      });
      setOldPoint(point);
    } else if (map && oldPoint?.uuid && markers[oldPoint.uuid]) {
      markers[oldPoint.uuid].setIcon(getMarkerIcon(oldPoint, null));
      setOldPoint(null);
    } else if (map) {
      loader.load().then(() => {
        const newMarkers: google.maps.Marker[] = [];
        disabledPoints.forEach(p => {
          if (p?.uuid && markers[p.uuid]) {
            markers[p.uuid].setOpacity(0);
          }
        });
        const pointsLatLng = getPointsLatLngForBounds();
        points.forEach(p => {
          if (p?.location?.latitude && p?.location?.longitude) {
            if (p?.uuid && markers[p.uuid]) {
              markers[p.uuid].setOpacity(1);
              markers[p.uuid].setIcon(getMarkerIcon(p, point?.uuid || null));
              newMarkers.push(markers[p.uuid]);
            }
          }
        });
        if (markerClusterer) {
          markerClusterer.clearMarkers();
          markerClusterer.addMarkers(newMarkers);
        }
        if (pointsLatLng.length > 0) {
          const boundsPoints = [...pointsLatLng];
          if (searchLocation?.lat && searchLocation?.lng) {
            boundsPoints.push(new google.maps.LatLng(searchLocation.lat, searchLocation.lng));
          }
          fitMapToPoints(map, boundsPoints);
        }
      });
    } else if (!map) {
      loader.load().then(async () => {
        const {
          google
        } = window;
        if (!googlemapRef.current) return;
        const newMap = new google.maps.Map(googlemapRef.current, {
          zoom: defaultZoom,
          center: defaultCenterLocation
        });
        if (!newMap) return;
        const newMarkers: Record<string, google.maps.Marker> = {};
        const newMarkersArray: google.maps.Marker[] = [];
        points.forEach(p => {
          if (p?.address && p?.location?.latitude && p?.location?.longitude) {
            const pointLocation = {
              lat: p.location.latitude,
              lng: p.location.longitude
            };
            const marker = new google.maps.Marker({
              map: newMap,
              position: pointLocation,
              title: p.name,
              icon: getMarkerIcon(p, point?.uuid || null),
              opacity: 1
            });
            if (p?.uuid) {
              marker.addListener('click', () => onClickMarker(p.uuid));
              newMarkers[p.uuid] = marker;
              newMarkersArray.push(marker);
            }
          }
        });
        if (points.length > 0) {
          markerClusterer = new MarkerClusterer({
            markers: newMarkersArray,
            map: newMap,
            algorithmOptions: {
              maxZoom: 14
            },
            renderer: getRenderMarkerClusterer(false)
          });
        }
        const pointsLatLng = getPointsLatLngForBounds();
        if (pointsLatLng.length > 0) {
          fitMapToPoints(newMap, pointsLatLng);
        } else {
          centerMapOnUserCountry(newMap);
        }
        if (points.length > 0) {
          setMap(newMap);
          setMarkers(newMarkers);
        }
      });
    }
  }, [points, point, disabledPoints, t]);
  return <div sx={styles.googleMap(active, size, getLayoutBarsTotalHeight())} id="map" ref={googlemapRef} />;
};
export default GoogleMap;